import React, { useState, useEffect } from 'react';
import {
  Table, Loader2, Eye, EyeOff, GripVertical, Save, RotateCcw, Columns
} from 'lucide-react';
import { tableauConfigAPI } from '../../services/api';

const WidgetTableauConfig = () => {
  const [colonnes, setColonnes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const colonnesRes = await tableauConfigAPI.getColonnesConfig();
      setColonnes(colonnesRes.data.colonnes || []);
    } catch (error) {
      console.error('Erreur chargement config tableau:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleColonneVisibility = (colonneId) => {
    setColonnes(prev => prev.map(col => 
      col.id === colonneId ? { ...col, visible: !col.visible } : col
    ));
    setHasChanges(true);
  };

  const saveColonnes = async () => {
    setSaving(true);
    try {
      await tableauConfigAPI.updateColonnesConfig(colonnes);
      setHasChanges(false);
    } catch (error) {
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const resetColonnes = async () => {
    if (!window.confirm('Réinitialiser la configuration des colonnes ?')) return;
    setSaving(true);
    try {
      await tableauConfigAPI.resetColonnesConfig();
      await loadData();
      setHasChanges(false);
    } catch (error) {
      alert('Erreur lors de la réinitialisation');
    } finally {
      setSaving(false);
    }
  };

  // Drag & Drop colonnes
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newList = [...colonnes];
    const draggedItem = newList[draggedIndex];
    newList.splice(draggedIndex, 1);
    newList.splice(index, 0, draggedItem);

    newList.forEach((item, idx) => {
      item.ordre = idx;
    });

    setColonnes(newList);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const categoriesColonnes = {
    base: 'Informations de base',
    contact: 'Contact',
    dossier: 'Dossier',
    travaux: 'Travaux',
    amo: 'AMO & ANAH',
    dates: 'Dates',
    suivi: 'Suivi'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-700 to-slate-900 rounded-xl p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Table className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Configuration du tableau</h2>
            <p className="text-slate-300">Personnalisez l'affichage de la liste des dossiers</p>
          </div>
        </div>
      </div>

      {/* Section Colonnes */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <Columns className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-gray-900">Colonnes affichées</h3>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Glissez-déposez les colonnes pour définir leur ordre dans le tableau.
          </p>
        </div>

        <div className="p-4">
          {/* Liste simple triée par ordre */}
          <div className="space-y-1">
            {[...colonnes].sort((a, b) => a.ordre - b.ordre).map((col, index) => (
              <div
                key={col.id}
                draggable
                onDragStart={(e) => handleDragStart(e, colonnes.findIndex(c => c.id === col.id))}
                onDragOver={(e) => handleDragOver(e, colonnes.findIndex(c => c.id === col.id))}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 p-3 rounded-lg border transition cursor-move ${
                  draggedIndex === colonnes.findIndex(c => c.id === col.id)
                    ? 'border-indigo-500 bg-indigo-100 shadow-lg'
                    : col.visible
                      ? 'border-indigo-200 bg-indigo-50 hover:border-indigo-300'
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-400 w-6 text-center">{index + 1}</span>
                  <GripVertical className="w-4 h-4 text-gray-400" />
                </div>
                <button
                  onClick={() => toggleColonneVisibility(col.id)}
                  className={`p-1.5 rounded-lg transition ${
                    col.visible
                      ? 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
                      : 'bg-gray-200 text-gray-400 hover:bg-gray-300'
                  }`}
                  title={col.visible ? 'Masquer cette colonne' : 'Afficher cette colonne'}
                >
                  {col.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <span className={`flex-1 font-medium ${col.visible ? 'text-gray-900' : 'text-gray-500'}`}>
                  {col.label}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  col.categorie === 'base' ? 'bg-blue-100 text-blue-600' :
                  col.categorie === 'contact' ? 'bg-green-100 text-green-600' :
                  col.categorie === 'dossier' ? 'bg-purple-100 text-purple-600' :
                  col.categorie === 'travaux' ? 'bg-amber-100 text-amber-600' :
                  col.categorie === 'amo' ? 'bg-pink-100 text-pink-600' :
                  col.categorie === 'dates' ? 'bg-cyan-100 text-cyan-600' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {categoriesColonnes[col.categorie] || col.categorie}
                </span>
                {col.visible && (
                  <span className="text-xs bg-indigo-600 text-white px-2 py-0.5 rounded font-semibold">
                    Visible
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-4 border-t border-gray-200 mt-4">
            <button
              onClick={saveColonnes}
              disabled={saving || !hasChanges}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </button>
            <button
              onClick={resetColonnes}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              title="Réinitialiser"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Info filtres rapides */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-700">
          <strong>💡 Astuce :</strong> Pour filtrer rapidement le tableau, survolez une valeur (Caisse, Statut, etc.) 
          dans la liste des dossiers et cliquez sur l'icône de filtre qui apparaît.
        </p>
      </div>
    </div>
  );
};

export default WidgetTableauConfig;
